import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  Target,
  Question,
  CaretDown,
  Eye,
  Lightbulb,
  Lightning,
  Timer,
  Heartbeat,
  Clock,
  ListChecks,
  ArrowsClockwise,
  Gear,
  Notebook,
  Crosshair,
  BookOpen,
  Scales,
  Path,
  Repeat,
  PuzzlePiece,
  Graph,
  UserFocus,
  Shuffle,
  GitBranch,
  HandFist
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const WhyConsistencyIsHard = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Why Consistency Is Hard in Trading | Trading Psychology Guide',
      description: 'Learn why consistency is difficult in trading and how to build better discipline through structure, psychology, and data.',
      path: '/why-consistency-is-hard-in-trading',
      lang: 'en',
      alternates: getArticleAlternates('/why-consistency-is-hard-in-trading'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "Why is consistency important in trading?",
      answer: "Consistency is what allows your edge to play out over time. A trading strategy only works if you execute it consistently—the same way, every time. Inconsistent execution introduces randomness that drowns out your actual edge. Without consistency, you can't evaluate whether your strategy works or whether you're just experiencing random variance. Consistent traders can diagnose problems; inconsistent traders never know what went wrong."
    },
    {
      question: "Why do traders struggle with discipline?",
      answer: "Discipline depletes with use—it's a limited mental resource. Traders who rely purely on willpower to follow their rules inevitably break down. Additionally, the market provides inconsistent feedback: sometimes breaking rules works, sometimes following them loses. This randomness makes discipline feel arbitrary. The solution isn't more willpower—it's building systems that make discipline automatic rather than effortful."
    },
    {
      question: "How can I become more consistent?",
      answer: "Start with structure: written rules, pre-session planning, post-trade review. Create checklists that must be completed before entry. Track every trade and review weekly for patterns. Set environmental constraints (max trades per day, mandatory breaks). Make following the process easier than breaking it. Consistency comes from systems, not willpower—design your trading to make inconsistency difficult."
    },
    {
      question: "Is consistency more important than strategy?",
      answer: "They're inseparable. A mediocre strategy executed consistently will outperform a great strategy executed inconsistently. Why? Because consistent execution produces usable data—you can see what's working and what isn't. Inconsistent execution produces noise that makes improvement impossible. The best traders prioritize consistency of process over perfection of strategy, knowing that consistent execution enables continuous improvement."
    },
    {
      question: "Can you learn consistency or is it a personality trait?",
      answer: "Consistency is a skill, not a trait. It can be developed through deliberate practice and environmental design. Some people have natural advantages in self-regulation, but anyone can build structures that support consistent behavior. The key insight: don't try to 'become' more disciplined—instead, design systems that make discipline the path of least resistance. Consistency is built, not born."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Brain size={16} weight="fill" />
            <span>Trading Psychology</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Why Consistency Is So Hard in Trading
            <span className="block text-white/60 text-xl md:text-2xl mt-2">
              (And How to Fix It)
            </span>
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            You know what you should do. You've read the books. You've studied the charts. 
            You've even written down your rules. And yet—you keep breaking them. You enter 
            trades you shouldn't. You exit too early. You hesitate when you should act. 
            <strong className="text-white"> The gap between knowing and doing</strong> is where 
            most trading accounts go to die.
          </p>
        </header>

        {/* Introduction */}
        <section className="mb-14 prose prose-invert max-w-none">
          <p className="text-white/70 leading-relaxed text-lg">
            <strong className="text-white">Consistency in trading</strong> is perhaps the most 
            elusive skill. It's not about finding the perfect strategy or the right indicator. 
            It's about executing the same process, the same way, day after day—even when it 
            feels wrong, even when you're scared, even when you're bored.
          </p>
          <p className="text-white/70 leading-relaxed text-lg">
            Understanding <strong className="text-white">why traders are inconsistent</strong> requires 
            looking beyond strategy into psychology, neuroscience, and behavioral patterns. 
            The problem isn't what you know—it's what you do with what you know.
          </p>
        </section>

        {/* Section 1: The Illusion of Knowing vs Doing */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">01</span>
            The Illusion of Knowing vs Doing
          </h2>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-8 mb-8">
            <p className="text-white/80 leading-relaxed mb-4">
              There's a dangerous assumption in trading education: that <strong className="text-white">understanding 
              equals ability</strong>. Read about risk management, and you'll manage risk well. 
              Learn about patience, and you'll be patient. But this isn't how humans work.
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                gap: "Understanding Strategies",
                reality: "Knowing what a good setup looks like doesn't mean you'll wait for one. Knowledge doesn't override impulse.",
                icon: BookOpen
              },
              {
                gap: "Failing to Execute",
                reality: "The moment emotions activate, rational knowledge takes a back seat. You know better, but you do worse.",
                icon: Lightning
              },
              {
                gap: "Knowledge ≠ Performance",
                reality: "Information is abundant. Execution is scarce. The trader who acts correctly beats the trader who knows more.",
                icon: Target
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-white/10 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.gap}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.reality}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              This is why <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline">traders fail to improve</Link> despite 
              consuming endless content. <strong className="text-white">Trading discipline</strong> isn't 
              about knowing more—it's about executing what you already know.
            </p>
          </div>
        </section>

        {/* Section 2: The Role of Emotions */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">02</span>
            The Role of Emotions in Inconsistency
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Emotions are the primary enemy of <strong className="text-white">trading psychology 
              consistency</strong>. They hijack your decision-making process, overriding the logical 
              systems you've worked hard to build.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                emotion: "Fear",
                behavior: "Hesitating on valid entries, exiting too early, avoiding trades entirely after losses",
                color: "blue"
              },
              {
                emotion: "Greed",
                behavior: "Oversizing positions, holding winners too long, adding to winners without plan",
                color: "yellow"
              },
              {
                emotion: "Hesitation",
                behavior: "Missing entries while 'waiting for confirmation,' analysis paralysis, second-guessing",
                color: "purple"
              },
              {
                emotion: "Overconfidence",
                behavior: "Increasing size after wins, relaxing rules, taking marginal setups that don't qualify",
                color: "orange"
              }
            ].map((item, index) => (
              <div 
                key={index}
                className={`bg-[#121212] border rounded-lg p-5 ${
                  item.color === 'blue' ? 'border-blue-500/20' :
                  item.color === 'yellow' ? 'border-yellow-500/20' :
                  item.color === 'purple' ? 'border-purple-500/20' :
                  'border-orange-500/20'
                }`}
              >
                <h4 className={`font-semibold mb-2 ${
                  item.color === 'blue' ? 'text-blue-400' :
                  item.color === 'yellow' ? 'text-yellow-400' :
                  item.color === 'purple' ? 'text-purple-400' :
                  'text-orange-400'
                }`}>{item.emotion}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{item.behavior}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Each emotion pushes you toward inconsistent behavior. The trader who felt confident 
              yesterday takes different actions than the trader who felt scared today—even if 
              the market setup is identical. This emotional variability is the core of inconsistency.
            </p>
          </div>
        </section>

        {/* Section 3: The Brain and Decision-Making */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-blue-400 text-lg font-mono">03</span>
            The Brain and Decision-Making
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Your brain isn't designed for trading. It evolved to survive immediate threats 
              and capture immediate rewards—neither of which aligns with the patient, 
              probabilistic thinking trading requires.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 rounded-lg p-6 mb-8">
            <div className="flex items-start gap-3 mb-4">
              <BookOpen size={24} weight="duotone" className="text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">"Thinking, Fast and Slow" — Daniel Kahneman</h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  Kahneman's framework explains why trading is psychologically difficult. Our 
                  brains operate with two systems:
                </p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div className="bg-[#121212]/50 rounded-lg p-4">
                <h5 className="font-semibold text-red-400 mb-2">System 1 (Fast)</h5>
                <ul className="space-y-1 text-white/60 text-sm">
                  <li>• Automatic, emotional</li>
                  <li>• Reacts to threats instantly</li>
                  <li>• Seeks immediate rewards</li>
                  <li>• Pattern-matches on incomplete data</li>
                  <li>• <span className="text-red-400">Drives impulsive trades</span></li>
                </ul>
              </div>
              <div className="bg-[#121212]/50 rounded-lg p-4">
                <h5 className="font-semibold text-green-400 mb-2">System 2 (Slow)</h5>
                <ul className="space-y-1 text-white/60 text-sm">
                  <li>• Deliberate, logical</li>
                  <li>• Evaluates evidence carefully</li>
                  <li>• Considers long-term outcomes</li>
                  <li>• Follows rules and plans</li>
                  <li>• <span className="text-green-400">Enables consistent execution</span></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Inconsistency is System 1 overpowering System 2.</strong> When 
              price spikes, System 1 screams "act now!" before System 2 can evaluate whether 
              the setup qualifies. Building consistency means building systems that force System 2 
              to engage before any action is taken.
            </p>
          </div>
        </section>

        {/* Section 4: Why Traders Break Their Own Rules */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">04</span>
            Why Traders Break Their Own Rules
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Every trader has rules. Few follow them consistently. The gap isn't about memory 
              or commitment—it's about the psychological forces working against you in real-time.
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                reason: "Impulsive Decisions",
                description: "The entry looks good. You feel it. You don't want to miss it. You click before checking your criteria. Later, you realize it didn't qualify.",
                icon: Lightning
              },
              {
                reason: "Reacting to Market Noise",
                description: "Every tick feels meaningful. A red candle triggers fear. A green candle triggers FOMO. You react to noise instead of waiting for signal.",
                icon: Graph
              },
              {
                reason: "Lack of True Discipline",
                description: "Discipline isn't a personality trait—it's a mental resource that depletes. After hours of watching charts, your discipline runs out. Rules feel optional.",
                icon: HandFist
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-red-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.reason}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <BookOpen size={24} weight="duotone" className="text-purple-400 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">"Trading in the Zone" — Mark Douglas</h4>
                <p className="text-white/70 text-sm leading-relaxed">
                  Douglas emphasizes that consistent trading requires <strong className="text-white">probability 
                  thinking</strong> and <strong className="text-white">emotional neutrality</strong>. You must 
                  accept that any single trade's outcome is uncertain—and be at peace with that 
                  uncertainty. Rules get broken when traders need trades to work, rather than 
                  accepting they might not.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: The Problem With Inconsistent Feedback */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-orange-400 text-lg font-mono">05</span>
            The Problem With Inconsistent Feedback
          </h2>
          
          <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed mb-4">
              In most skills, feedback is direct: practice piano correctly, it sounds good. 
              Practice incorrectly, it sounds bad. But trading gives you <strong className="text-white">inconsistent 
              feedback</strong> that confuses your learning process.
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-green-400 flex items-center gap-2">
                  <CheckCircle size={18} />
                  Good Process, Bad Outcome
                </h4>
                <p className="text-white/60 text-sm leading-relaxed mb-3">
                  You follow your rules perfectly. The setup is valid. You execute well. 
                  The trade loses anyway.
                </p>
                <p className="text-white/50 text-xs">
                  Result: You question your rules even though you did nothing wrong.
                </p>
              </div>
              <div className="p-6">
                <h4 className="font-semibold mb-4 text-red-400 flex items-center gap-2">
                  <Warning size={18} />
                  Bad Process, Good Outcome
                </h4>
                <p className="text-white/60 text-sm leading-relaxed mb-3">
                  You break your rules. Enter impulsively. Ignore your stop. 
                  The trade wins anyway.
                </p>
                <p className="text-white/50 text-xs">
                  Result: Breaking rules gets reinforced. "Maybe rules don't matter."
                </p>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              This randomness makes <strong className="text-white">consistency in trading</strong> feel 
              irrational. Why follow rules when breaking them sometimes works? The answer is 
              statistical—rules create edge over many trades—but emotionally, each random outcome 
              chips away at your commitment. This is why <Link to="/overtrading-explained" className="text-[#00E676] hover:underline">overtrading</Link> becomes 
              so tempting.
            </p>
          </div>
        </section>

        {/* Section 6: Lack of Structure */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-red-400 text-lg font-mono">06</span>
            Lack of Structure
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Many traders have vague ideas about what they should do but no concrete structure 
              for doing it. Without structure, every session becomes an improvisation—and 
              improvisation under pressure leads to inconsistency.
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                problem: "No Clear Process",
                description: "No pre-session routine. No defined criteria. Every trade is a fresh decision made under emotional pressure.",
                icon: Shuffle
              },
              {
                problem: "No Review System",
                description: "Trades happen and are forgotten. No logging, no analysis, no feedback loop. Mistakes repeat because they're never examined.",
                icon: Eye
              },
              {
                problem: "No Performance Tracking",
                description: "You don't know your win rate, profit factor, or which setups work. Without data, improvement is guesswork.",
                icon: ChartLineUp
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-red-500/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-red-400" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.problem}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Structure is the scaffolding that holds consistency together. Without it, you're 
              relying entirely on willpower and memory—neither of which is reliable under stress.
            </p>
          </div>
        </section>

        {/* Section 7: Why Consistency Requires a System */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">07</span>
            Why Consistency Requires a System
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              You can't willpower your way to consistency. You need systems that make consistent 
              behavior the default—that make following rules easier than breaking them.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              {
                element: "Clear Rules",
                description: "Written criteria for what qualifies as a trade. If it doesn't match, you don't trade—period.",
                icon: ListChecks
              },
              {
                element: "Repetition",
                description: "Same process, every session. Pre-market routine, entry checklist, post-trade review. Repetition builds habit.",
                icon: Repeat
              },
              {
                element: "Feedback Loops",
                description: "Track every trade. Review weekly. Identify patterns. Adjust based on data, not feelings.",
                icon: ArrowsClockwise
              },
              {
                element: "Data-Driven Decisions",
                description: "Let numbers guide changes. Which setups work? Which times? Which conditions? Data removes emotion from optimization.",
                icon: Graph
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-[#00E676]/20 rounded-lg p-5"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2">{item.element}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Systems don't require motivation. They just require following steps. When you're 
              tired, scared, or overconfident, the system still works—because it doesn't depend 
              on how you feel.
            </p>
          </div>
        </section>

        {/* Section 8: From Emotional to Structured Execution */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">08</span>
            From Emotional Trading to Structured Execution
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              The transformation from inconsistent to consistent isn't a single decision—it's 
              a gradual shift in how you approach every session. Here's what the transition 
              looks like:
            </p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden mb-8">
            <div className="p-4 bg-white/5 border-b border-white/10">
              <h4 className="font-semibold text-sm text-white/60">The Evolution</h4>
            </div>
            <div className="p-6 space-y-4">
              {[
                { from: "Reactive", to: "Systematic", desc: "Responding to market → Following predetermined rules" },
                { from: "Random", to: "Intentional", desc: "Trading because you feel like it → Trading because criteria are met" },
                { from: "Inconsistent", to: "Repeatable", desc: "Different approach daily → Same process every session" },
                { from: "Emotional", to: "Process-Driven", desc: "Feelings guide decisions → Rules guide decisions" },
                { from: "Outcome-Focused", to: "Process-Focused", desc: "Did I make money? → Did I follow my system?" }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <span className="text-red-400 text-sm font-medium w-28">{item.from}</span>
                  <ArrowRight size={16} className="text-white/30" />
                  <span className="text-[#00E676] text-sm font-medium w-28">{item.to}</span>
                  <span className="text-white/50 text-xs flex-1">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              This evolution is what <Link to="/how-to-improve-day-trading" className="text-[#00E676] hover:underline">improving in day trading</Link> actually 
              looks like. It's not about finding better setups—it's about becoming someone who 
              executes their setups reliably.
            </p>
          </div>
        </section>

        {/* Section 9: What Actually Builds Consistency */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">09</span>
            What Actually Builds Consistency
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4 mb-8">
            <p className="text-white/70 leading-relaxed">
              Consistency isn't built through willpower alone. It's constructed through 
              deliberate practices that create accountability and awareness:
            </p>
          </div>
          
          <div className="space-y-4 mb-8">
            {[
              {
                practice: "Reviewing Trades Regularly",
                description: "Weekly review sessions where you analyze what you did, why, and what happened. Patterns become visible. Mistakes become obvious.",
                icon: Eye
              },
              {
                practice: "Tracking Performance",
                description: "Logging every trade with consistent data points. Win rate, R:R, setup type, emotional state. You can't improve what you don't measure.",
                icon: Notebook
              },
              {
                practice: "Refining Setups",
                description: "Using data to identify which setups work and which don't. Eliminating the losers. Doubling down on the winners.",
                icon: Crosshair
              },
              {
                practice: "Controlling Behavior",
                description: "Setting constraints: max trades per day, mandatory breaks after losses, checklists before entry. Structure creates consistency.",
                icon: UserFocus
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-[#00E676]/20 rounded-lg p-6"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-[#00E676]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.practice}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Many traders use tools like <strong className="text-white">GENI</strong> to create 
              structured feedback loops, making it easier to stay consistent and improve over 
              time. Learning <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline">how to analyze your trades</Link> is 
              the foundation of this approach.
            </p>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-14">
          <div className="bg-gradient-to-br from-[#00E676]/10 to-transparent border border-[#00E676]/30 rounded-lg p-8">
            <h3 className="text-xl font-bold mb-4">Final Thoughts</h3>
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">Consistency in trading</strong> is not about perfection. 
              It's about building systems that help you make better decisions over time. Some days 
              you'll slip. Some weeks you'll struggle. The goal isn't never failing—it's creating 
              structures that pull you back when you drift.
            </p>
            <p className="text-white/70 leading-relaxed">
              The traders who succeed long-term aren't the ones with perfect discipline. They're 
              the ones who built environments where consistency is the path of least resistance. 
              Design your trading to support consistency, and consistency will follow.
            </p>
          </div>
        </section>

        {/* Internal Links */}
        <section className="mb-14">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Related Articles:</strong>
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Link to="/overtrading-explained" className="text-[#00E676] hover:underline text-sm">
                Overtrading Explained →
              </Link>
              <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline text-sm">
                Why Traders Fail to Improve →
              </Link>
              <Link to="/how-to-improve-day-trading" className="text-[#00E676] hover:underline text-sm">
                How to Improve in Day Trading →
              </Link>
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline text-sm">
                How to Analyze Your Trades →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-14">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/overtrading-explained" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Overtrading Explained</h3>
              <p className="text-white/50 text-sm">Why traders overtrade and how to stop.</p>
            </Link>
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck.</p>
            </Link>
            <Link to="/how-to-improve-day-trading" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Improve in Day Trading</h3>
              <p className="text-white/50 text-sm">What actually works for getting better.</p>
            </Link>
            <Link to="/how-to-analyze-your-trades" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Analyze Your Trades</h3>
              <p className="text-white/50 text-sm">Step-by-step guide to trade review.</p>
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Soft CTA */}
        <section>
          <div className="bg-gradient-to-br from-[#00E676]/10 via-[#121212] to-purple-500/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <ArrowsClockwise size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Build Your Consistency System
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              Track your trades, identify patterns, and build the feedback loops that 
              turn inconsistent trading into repeatable performance.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Tracking Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "Why Consistency Is So Hard in Trading (And How to Fix It)",
              "description": "Learn why consistency is difficult in trading and how to build better discipline through structure, psychology, and data.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/why-consistency-is-hard-in-trading"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WhyConsistencyIsHard;
